import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";

import { DSQConflitingMedication } from "@/Common/Api/Dsq/Dto/Responses/DSQConflictingMedications";
import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { PrescriptionPrintHistoryClient } from "@/Common/Api/Patient/PrescriptionPrintHistoryClient";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { PaginationViewControl } from "@/Common/Components/Pagination/PaginationViewControl";
import { PdfPageDetails, PdfViewer } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { SlideMenuBar } from "@/Common/Components/Sidebar/SlideMenuBar";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PatientBannerHelper } from "@/Common/Helpers/PatientBannerHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Guid } from "@/Common/Models/Guid";
import { Note } from "@/Common/Models/Note";
import { Patient } from "@/Common/Models/Patient/Patient";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { ExternalMessageFormPopupComponent } from "@/Communications/Components/ExternalCommunications/Form/ExternalMessageFormPopupComponent";
import { PreferredPharmacyHelper } from "@/ContactDirectory/Helpers/PreferredPharmacyHelper";
import { DocumentNote } from "@/Documents/Api/Dto/Response/DocumentNote";
import { DocumentNotesPopup } from "@/Documents/Components/DocumentNotes/DocumentNotesPopup";
import { ReturnFaxToSenderPopup } from "@/Documents/Components/InboundFax/ReturnFaxToSenderPopup";
import { OutboundFaxCreationPopup } from "@/Documents/Components/OutboundFax/OutboundFaxCreationPopup";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { DocumentCreateEditUIModel, IDocumentCreateEditUIModel, IDocumentInformation } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { SendDocumentStrategy } from "@/Documents/Services/Portal/Strategies/SendDocumentStrategy";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { IExportableFileLoaded } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FileTypeDocument } from "@/ExportableFile/Models/FileTypes/FileTypeDocument";
import { FileTypeDocumentBytes } from "@/ExportableFile/Models/FileTypes/FileTypeDocumentBytes";
import { FileTypePrescription } from "@/ExportableFile/Models/FileTypes/FileTypePrescription";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { PatientBannerForLabs } from "@/Labs/Components/PatientBannerForLabs";
import { PatientPrescriptionTab } from "@/Patient/Components/Medication/PatientPrescriptionTab";
import { VitalSignsView } from "@/Patient/Components/VitalSigns/VitalSignsView";
import { MedicalItemHelper } from "@/Patient/Helpers/MedicalItemHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PrescriptionPrintHistory } from "@/Patient/Models/Medication/PrescriptionPrintHistory";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IPrescriptionTabModel } from "@/Patient/Models/UI/Medication/IPrescriptionTabModel";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { GetCumulativePatientProfileByPrescriptionRenewalResponse } from "../Api/Dto/GetCumulativePatientProfileByPrescriptionRenewalResponse";
import { GetPrescriptionRenewalByIdResponse } from "../Api/Dto/GetPrescriptionRenewalByIdResponse";
import { PrescriptionRenewalMedicationsResponse } from "../Api/Dto/PrescriptionRenewalMedicationsResponse";
import { completePrescriptionRenewal, fetchCumulativePatientProfile, fetchMedicalDocumentData, fetchOntarioPatientEnrollmentModel, fetchPrescriptionRenewalById, fetchPrescriptionsRenewalMedications, onPdfPopoutClick, returnDocumentWithComment, savePrescriptionRenewal, updateDocumentInformation } from "../Api/PrescriptionRenewalsClient";
import { PrescriptionRenewalStatus } from "../Common/PrescriptionRenewalStatus";
import UsePrescriptionRenewalPagination from "../CustomHook/UsePrescriptionRenewalPagination";
import { EnumPrescriptionRenewalMedicationsStatus } from "../Models/EnumPrescriptionRenewalMedicationsStatus";
import { IPrescriptionRenewalsViewContextValueModel } from "../Models/IPrescriptionRenewalsViewContextValueModel";
import { PrescriptionRenewal } from "../Models/PrescriptionRenewal";
import { PrescriptionRenewalCurrentDocument } from "../Models/PrescriptionRenewalCurrentDocument";
import { PrescriptionRenewalInboxSettings } from "../Models/PrescriptionRenewalInboxSettings";
import { PrescriptionRenewalMedications } from "../Models/PrescriptionRenewalMedications";
import { PrescriptionRenewalsViewModel } from "../Models/PrescriptionRenewalsViewModel";
import { PrescriptionRenewalExportableFileFaxPopup } from "./PrescriptionRenewalExportableFileFaxPopup";
import { PrescriptionRenewalsViewDenyPopup } from "./PrescriptionRenewalsViewDenyPopup";
import { PrescriptionRenewalsViewEditMedicationPopup } from "./PrescriptionRenewalsViewEditMedicationPopup";
import { PrescriptionRenewalsViewNavigationBar } from "./PrescriptionRenewalsViewNavigationBar";
import { PrescriptionRenewalsViewRoutes } from "./PrescriptionRenewalsViewRoutes";

import styles from "./PrescriptionRenewalsView.module.scss";

interface IPrescriptionRenewalsViewProps {
    prescriptionRenewalInboxSettings: PrescriptionRenewalInboxSettings,
    onCancelClick: (queryCurrentPageNumber: number) => void
}

const PrescriptionRenewalsViewContext = createContext<IPrescriptionRenewalsViewContextValueModel>(null);
const INVALID_PDF_ZOOM_LEVEL_INDEX: number = -1;

function PrescriptionRenewalsView(props: IPrescriptionRenewalsViewProps): JSX.Element {
    // TODO: add the Prescription renewals view subcomponents
    const { prescriptionRenewalInboxSettings } = props;
    const { prescriptionRenewalId } = useParams();
    const navigate = useNavigate();

    const {
        prescriptionRenewalsCount,
        prescriptionRenewalRequestData,
        isLastPrescriptionRenewal,
        isPreviousDisabled,
        loadPrescriptionRenewalsInfo,
        getCurrentPrescriptionRenewalIndexFromTotal,
        onPreviousPrescriptionRenewal,
        onNextPrescriptionRenewal
    } = UsePrescriptionRenewalPagination(prescriptionRenewalId, prescriptionRenewalInboxSettings);

    const { getText } = useTranslation();

    const [relatedPrescriptionRenewalMedications, setRelatedPrescriptionRenewalMedications] = useState<PrescriptionRenewalMedications[]>([]);
    const [prescriptionState, setPrescriptionState] = useState<IPrescriptionTabModel>(() => Immutable.fromJS(PatientPrescriptionTab.GetDefaultPatientPrescriptionTabModel()));
    const [patientBannerModel, setPatientBanner] = useState<IPatientBannerModel>(null);
    const [isUrgent, setUrgent] = useState<boolean>(false);
    const [currentDocument, setCurrentDocument] = useState<PrescriptionRenewalCurrentDocument>(new PrescriptionRenewalCurrentDocument(null));
    const [documentCreateEditUIModel, setDocumentCreateEditUIModel] = useState<IDocumentCreateEditUIModel>(null);
    const pdfViewerContainerRef = useRef<HTMLDivElement>(null);
    const [medicalDocumentData, setMedicalDocumentData] = useState<MedicalDocumentItem>();
    const [isPortalPopupOpen, setIsPortalPopupOpen] = useState<boolean>();
    const [pdfSource, setPdfSource] = useState<string>("");
    const [pagesWithDetails, setPageWithDetails] = useState<PdfPageDetails[]>([]);
    const [pdfZoomLevelIndex, setPdfZoomLevelIndex] = useState<number>(INVALID_PDF_ZOOM_LEVEL_INDEX);
    const [isPdfViewerExpanded, setIsPdfViewerExpanded] = useState<boolean>(prescriptionRenewalsStore?.isPrescriptionRenewalsViewPdfViewerExpanded ?? true);
    const [patientCpp, setPatientCpp] = useState<IPatientMedicalSummaryModel>(null);
    const [cleanPatientCpp, setCleanPatientCpp] = useState<IPatientMedicalSummaryModel>(null);
    const [patientProfile, setPatientProfile] = useState<Patient>(null);
    const [latestVitalSign, setLatestVitalSign] = useState<MedicalItemVitalSign>(null);
    const [relatedMedicationToDeny, setRelatedMedicationToDeny] = useState<PrescriptionRenewalMedications>(null);
    const [prescriptionRenewalStatus, setPrescriptionRenewalStatus] = useState<PrescriptionRenewalStatus>(null);
    const [prescriptionRenewalNote, setPrescriptionRenewalNote] = useState<string>(null);
    const [medicationModelToUpdate, setMedicationModelToUpdate] = useState<MedicationModel>(null);
    const [isFaxAndPrintPopupOpen, setIsFaxAndPrintPopupOpen] = useState<boolean>(false);
    const [isDocumentDetailsPopupOpen, setIsDocumentDetailsPopupOpen] = useState<boolean>(false);
    const [exportableFileConfig, setExportableFileConfig] = useState<IFaxPrintExportableFileConfig>(null);
    const [exportMode, setExportMode] = useState<ExportMode>(ExportMode.Sign);
    const [encounterId, setEncounterId] = useState<Guid>(GuidHelper.Empty);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isPrescriptionRenewalPrescribed, setIsPrescriptionRenewalPrescribed] = useState<boolean>(false);
    const [hasDsqMedications, setHasDsqMedications] = useState<boolean>(false);
    const [senderFaxNumber, setSenderFaxNumber] = useState<string>("");
    const [isReturnWithCommentPopupOpen, setIsReturnWithCommentPopupOpen] = useState<boolean>(false);
    const [isExternalCommunicationVisible, setIsExternalCommunicationVisible] = useState<boolean>(false);
    const [exportableDocumentByteFileModel, setExportableDocumentByteFileModel] = useState<ExportableFileModel>(null);
    const latestIsDirty = useRef<boolean>(false);
    const latestPrescriptionTabModel = useRef<IPrescriptionTabModel>(null);
    const latestPrescriptionRenewalMedications = useRef<PrescriptionRenewalMedications[]>([]);
    const latestPrescriptionRenewalNote = useRef<string>(null);

    const onSetDefaultZoomLevel = useCallback((zoomLevels: number[], defaultPageWidth: number) => {
        if (pdfViewerContainerRef?.current && pdfZoomLevelIndex === INVALID_PDF_ZOOM_LEVEL_INDEX) {
            const pageContainerPaddingX = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--pdf-viewer-page-container-x"));
            const parsedLeftAndRightPaddingX = isNaN(pageContainerPaddingX) ? 0 : (pageContainerPaddingX * 2);
            const pdfViewerContainerWidth = pdfViewerContainerRef.current.clientWidth - parsedLeftAndRightPaddingX;

            for (let i = 0; i < zoomLevels.length; i++) {
                const expectedPageWidth = zoomLevels[i] * defaultPageWidth;

                if (pdfViewerContainerWidth < expectedPageWidth) {
                    setPdfZoomLevelIndex(Math.max(0, i - 1));
                    break;
                }
            }
        }
    }, [pdfZoomLevelIndex]);

    const setPatientData = (
        patientResponse: GetCumulativePatientProfileByPrescriptionRenewalResponse,
        setOnlyCleanData: boolean,
        ontarioPatientEnrollmentModel: OntarioPatientEnrollmentModel | null
    ): void => {
        const patientSummary = PatientMedicalSummaryHelper.getImmutableMedicalSummaryFromResponse(patientResponse.CumulativePatientProfile, null);
        const patient = Patient.fromResponseData(patientResponse.PatientProfile);
        PatientBannerHelper.loadRamqInfo(patient);

        if (ontarioPatientEnrollmentModel) {
            patient.OntarioPatientEnrollmentModel = ontarioPatientEnrollmentModel;
        }

        if (!setOnlyCleanData)
            setPatientCpp(patientSummary);

        setCleanPatientCpp(patientSummary);
        setPatientProfile(patient);

        if (patient !== null) {
            updateSecondScreen(patient.IndividualResponse?.Contact_id);
            const patientBannerDefault = {
                Model: patient,
                PatientDropdownVisible: false,
                Warnings: []
            };

            const patientBannerModel: IPatientBannerModel = Immutable.fromJS(patientBannerDefault);
            setPatientBanner(patientBannerModel);
        }
    };

    const setPrescriptionRenewalsViewDetail = (prescriptionResponse: GetPrescriptionRenewalByIdResponse, medicalDocument: MedicalDocumentItem, loadedExportableFile: IExportableFileLoaded = null): PrescriptionRenewalMedications[] => {
        const prescriptionRenewalDetails: PrescriptionRenewalsViewModel = PrescriptionRenewalsViewModel.fromResponseModel(prescriptionResponse);
        const { documentId, documentByteId } = prescriptionRenewalDetails;

        if (medicalDocument) {
            const exportableFileAbsoluteUrl = apiSettings.buildAppUrl(loadedExportableFile.FetchUrl, { auth: false });
            setPdfSource(exportableFileAbsoluteUrl); // ideally we set the exportableFile here and we dont preload it (getFetchUrlDtoAsync) until we really need to render it.

            const documentSubClass: string = cacheStore.GetLocalizedValueByLookupId(medicalDocument.Model.SubClassId) ?? "";
            const fileTypeDocumentDescription: string = FileTypeDocument.getDocumentFileTypeDescription(
                medicalDocument.getDefaultSubject(),
                medicalDocument.Model?.DocumentPathList?.[0]?.OriginalFileName ?? "",
                documentSubClass
            );
            setExportableDocumentByteFileModel(
                new FileTypeDocument({
                    DocumentId: `${documentByteId}`,
                    description: fileTypeDocumentDescription,
                    date: medicalDocument.Model.ReceiptDate?.toString()
                }).toExportableFileModel()
            );
        }

        setCurrentDocument({ documentId, documentByteId });
        setUrgent(prescriptionRenewalDetails.isUrgent);
        setPrescriptionRenewalStatus(prescriptionRenewalDetails.status);
        setPrescriptionRenewalNote(prescriptionRenewalDetails.notes);
        setSenderFaxNumber(prescriptionRenewalDetails.senderFaxNumber);

        return prescriptionRenewalDetails.relatedPrescriptionRenewalMedications;
    };

    const setPrescriptionMedications = (medications: PrescriptionRenewalMedicationsResponse): void => {
        const medicationModels: MedicationModel[] = medications?.MedicalItemsMedicationAndPrescription?.map(medicationItem => {
            medicationItem.Dosages = Immutable.List(medicationItem.Dosages.map(dosage => new MedicalItemMedicationAndPrescriptionDosage(dosage)));
            return new MedicationModel(medicationItem);
        }) ?? [];

        setPrescriptionState(p =>
            p.set("MedicationModels", Immutable.List(medicationModels))
                .set("VigilanceNumberOfAlerts", null)
        );
    };

    const loadPrescriptionRenewalsView = (): void => {
        const { prescriptionTabModel, prescriptionRenewalMedications, prescriptionRenewalNote, isDirty: isPrescriptionRenewalUpdated } = prescriptionRenewalsStore.prescriptionRenewalCachedModel[prescriptionRenewalId];
        setPrescriptionState(CommonHelper.getCloneDeep(prescriptionTabModel));
        setRelatedPrescriptionRenewalMedications(CommonHelper.getCloneDeep(prescriptionRenewalMedications));
        setPrescriptionRenewalNote(prescriptionRenewalNote);
        setIsDirty(isPrescriptionRenewalUpdated);
    };

    const updateSecondScreen = (patientContactId: Guid): void => {
        if (patientContactId) {
            if (SecondScreenNoteService.getInstance().isChildWindowOpen()) {
                SecondScreenNoteService.getInstance().debounceUpdateModel(new SecondScreenNoteData({ patientId: patientContactId }));
            }
        }
    };

    const getOntarioPatientEnrollmentModel = async (patientContactFacetId: string): Promise<OntarioPatientEnrollmentModel | null> => {
        if (!patientContactFacetId) {
            return;
        }

        if (!cacheStore.ClinicSettings.EnableOntarioPatientEnrollment ||
            !sessionStore.getCurrentEmployee().getActiveSite()?.isSiteInProvince(CanadaState.Ontario)
        ) {
            return;
        }

        return await fetchOntarioPatientEnrollmentModel(patientContactFacetId);
    };

    useEffect(() => {
        let isActive = true;

        setPatientCpp(null);
        setPatientProfile(null);
        setPatientBanner(null);

        (async () => {
            const prescriptionResponse: GetPrescriptionRenewalByIdResponse = await fetchPrescriptionRenewalById(prescriptionRenewalId);
            const medicalDocument = await fetchDocumentData(prescriptionResponse.DocumentId);
            let loadedExportableFile = null;
            if (medicalDocument) {
                const exportableFile = new FileTypeDocumentBytes({
                    DocumentBytesId: `${prescriptionResponse.DocumentByteId}`,
                    description: medicalDocument.getDefaultSubject(),
                    date: medicalDocument.Model.ReceiptDate?.toString()
                });

                loadedExportableFile = await exportableFile.getFetchUrlDtoAsync();
            }

            let relatedPrescriptionRenewalMedications = null;
            if (isActive)
                relatedPrescriptionRenewalMedications = setPrescriptionRenewalsViewDetail(prescriptionResponse, medicalDocument, loadedExportableFile);

            if (!prescriptionRenewalsStore?.prescriptionRenewalCachedModel?.[prescriptionRenewalId]) {
                const medications = await fetchPrescriptionsRenewalMedications(prescriptionRenewalId);
                if (isActive) {
                    setPrescriptionMedications(medications);
                    setRelatedPrescriptionRenewalMedications(relatedPrescriptionRenewalMedications);
                    setPrescriptionState(p => p.set("SearchText", "").set("SearchTextEventId", ""));
                    setIsDirty(false);
                }
            }
            else {
                loadPrescriptionRenewalsView();
            }
            const patientData: GetCumulativePatientProfileByPrescriptionRenewalResponse = await fetchCumulativePatientProfile(prescriptionRenewalId);
            const ontarioPatientEnrollmentModel = await getOntarioPatientEnrollmentModel(patientData?.CumulativePatientProfile?.PatientInactivableId);
            if (isActive) {
                setPatientData(patientData, false, ontarioPatientEnrollmentModel);
            }
        })();

        return () => {
            isActive = false;
            sessionStore.dispatchAsync(
                LocalOperations.PrescriptionRenewal.SetPrescriptionRenewalCachedModel,
                {
                    prescriptionRenewalId: prescriptionRenewalId,
                    prescriptionTabModel: latestPrescriptionTabModel.current,
                    prescriptionRenewalMedications: latestPrescriptionRenewalMedications.current,
                    prescriptionRenewalNote: latestPrescriptionRenewalNote.current,
                    isDirty: latestIsDirty.current
                }
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prescriptionRenewalId]);

    useEffect(() => {
        latestPrescriptionTabModel.current = prescriptionState;
        setHasDsqMedications(!!prescriptionState?.get("DSQMedicationModels")?.toArray()
            ?.any(medication => medication.Model.PrescriptionTab_IsRenew || !!medication.Model.PrescriptionTab_PendingStatus)
        );
    }, [prescriptionState]);

    useEffect(() => {
        latestPrescriptionRenewalMedications.current = relatedPrescriptionRenewalMedications;
    }, [relatedPrescriptionRenewalMedications]);

    useEffect(() => {
        latestPrescriptionRenewalNote.current = prescriptionRenewalNote;
    }, [prescriptionRenewalNote]);

    useEffect(() => {
        latestIsDirty.current = isDirty;
    }, [isDirty]);

    useEffect(() => {
        sessionStore.dispatch(
            LocalOperations.PrescriptionRenewal.SavePrescriptionRenewalsViewTogglerSession,
            {
                isPrescriptionRenewalsViewPdfViewerExpanded: isPdfViewerExpanded
            }
        );
    }, [isPdfViewerExpanded]);

    useEffect(() => {
        setLatestVitalSign(MedicalItemHelper.getLatestVitalSign(patientCpp?.get("MedicalItemsVitalSign")));
    }, [patientCpp?.get("MedicalItemsVitalSign")]);

    useEffect(() => {
        loadPrescriptionRenewalsInfo();
    }, [loadPrescriptionRenewalsInfo]);

    const fetchDocumentData = async (documentId: Guid): Promise<MedicalDocumentItem> => {
        const medicalDocument = await fetchMedicalDocumentData(documentId);
        const medicalDocumentItem = new MedicalDocumentItem(medicalDocument);
        setMedicalDocumentData(medicalDocumentItem);
        setDocumentCreateEditUIModel(DocumentCreateEditUIModel.fromResponseModel(medicalDocument));

        return medicalDocumentItem;
    };

    const onPortalStatusClicked = (): void => {
        setIsPortalPopupOpen(!isPortalPopupOpen);
    };

    const onSavePrescriptionRenewalDetails = async (): Promise<void> => {
        const medicationModels = prescriptionState?.get("MedicationModels")?.toArray() ?? [];
        const medicationItems: MedicalItemMedicationAndPrescription[] = medicationModels.map(
            medicationModel => new MedicalItemMedicationAndPrescription({
                ...medicationModel.Model,
                CeaseDate: medicationModel.PrescriptionTab_OriginalMedication?.Model.CeaseDate,
                CeaseReason: medicationModel.PrescriptionTab_OriginalMedication?.Model.CeaseReason,
                DiscontinueReasonLookupId: medicationModel.PrescriptionTab_OriginalMedication?.Model.DiscontinueReasonLookupId,
                CeaseEmployeeId: medicationModel.PrescriptionTab_OriginalMedication?.Model.CeaseEmployeeId
            })
        ) ?? [];

        const patientContactId = patientCpp.get("PatientContactId");
        await savePrescriptionRenewal(
            prescriptionRenewalId,
            patientContactId,
            prescriptionRenewalNote,
            medicationItems,
            relatedPrescriptionRenewalMedications
        );

        setIsDirty(false);
    };

    const navigateNextPrescriptionRenewal = (): void => {
        if (isLastPrescriptionRenewal)
            navigateToPrescriptionRenewalInbox();
        else
            onNextPrescriptionRenewal();
    };

    const navigateToPrescriptionRenewalInbox = (): void => {
        const firstPrescriptionRenewalPage = 1;
        props.onCancelClick(firstPrescriptionRenewalPage);
        navigate("/prescriptionrenewals");
    };

    const onDenyPopupClose = (): void => {
        setRelatedMedicationToDeny(null);
    };

    const onDenyPopupSave = (rejectReason: string, rejectNote: string): void => {
        setRelatedPrescriptionRenewalMedications(
            relatedMedications => relatedMedications.map(relatedMedication => {
                if (relatedMedication.medicationId === relatedMedicationToDeny.medicationId) {
                    return new PrescriptionRenewalMedications({
                        ...relatedMedicationToDeny,
                        rejectReason: rejectReason,
                        rejectNotes: rejectNote,
                        status: EnumPrescriptionRenewalMedicationsStatus.Denied
                    });
                }
                else {
                    return relatedMedication;
                }
            })
        );

        setIsDirty(true);
        onDenyPopupClose();
    };

    const onDenyClick = useCallback((medicationId: Guid): void => {
        const relatedMedicationToDeny = relatedPrescriptionRenewalMedications.find(p => p.medicationId === medicationId);

        if (!relatedMedicationToDeny) {
            return;
        }

        setRelatedMedicationToDeny(new PrescriptionRenewalMedications({ ...relatedMedicationToDeny }));
    }, [relatedPrescriptionRenewalMedications]);

    const addRelatedPrescriptionRenewalMedications = useCallback((medicationsIdToPopulate: Guid[]) => {
        const uniqueMedicationsId: Guid[] = medicationsIdToPopulate.filter(medicationId =>
            relatedPrescriptionRenewalMedications?.every(relatedMedication => relatedMedication.medicationId !== medicationId)
        );

        if (!uniqueMedicationsId.any()) {
            return;
        }

        const relatedMedications: PrescriptionRenewalMedications[] = uniqueMedicationsId.map(medicationId => new PrescriptionRenewalMedications({
            medicationId: medicationId,
            status: EnumPrescriptionRenewalMedicationsStatus.Approved
        }));

        setRelatedPrescriptionRenewalMedications(currentRelatedMedications => currentRelatedMedications.concat(...relatedMedications));
    }, [relatedPrescriptionRenewalMedications]);

    const removeRelatedPrescriptionRenewalMedications = useCallback((medicationsIdToKeep: Guid[]) => {
        setRelatedPrescriptionRenewalMedications(currentRelatedMedications => currentRelatedMedications?.filter(relatedMedication =>
            medicationsIdToKeep.some((medicationIdToRemove: Guid) => relatedMedication.medicationId === medicationIdToRemove)
        ));
    }, []);

    const onEditMedicationPopupOpen = useCallback((medicationId: string) => {
        const medicationModel: MedicationModel = prescriptionState?.get("MedicationModels")
            ?.filter(p => p.Model.Id === medicationId).first()?.Clone(false, false, true) ?? null;

        setMedicationModelToUpdate(medicationModel);
    }, [prescriptionState]);

    const onEditMedicationPopupClose = (): void => {
        setMedicationModelToUpdate(null);
    };

    const returnWithComment = async (faxDetails: InboundFaxDetails, onCompleted?: VoidFunction): Promise<void> => {
        const response = await returnDocumentWithComment(
            faxDetails,
            patientProfile?.IndividualResponse?.ContactFacetId ?? null,
            currentDocument?.documentId ?? null,
            currentDocument?.documentByteId ?? null
        );

        if (response.isSuccess) {
            setSenderFaxNumber("");
            setIsReturnWithCommentPopupOpen(false);
            navigateNextPrescriptionRenewal();
        }
        else if (response.errors.any()) {
            sessionStore.showPopup({
                content: response.errors,
                okAction: () => onCompleted?.()
            });
        }
    };

    const isPrescriptionRenewalCompleted = useMemo(
        () => PrescriptionRenewal.isPrescriptionRenewalCompleted(prescriptionRenewalStatus),
        [prescriptionRenewalStatus]
    );
    const areAllMedicationsDenied: boolean = relatedPrescriptionRenewalMedications.all(p => p.status === EnumPrescriptionRenewalMedicationsStatus.Denied);
    const isButtonDisabled = (): boolean => !relatedPrescriptionRenewalMedications.any() || prescriptionRenewalStatus === PrescriptionRenewalStatus.Completed || isPrescriptionRenewalPrescribed;

    const prescriptionRenewalsViewContextValue: IPrescriptionRenewalsViewContextValueModel = useMemo(() => ({
        relatedPrescriptionRenewalMedications,
        prescriptionState,
        isPrescriptionRenewalCompleted,
        prescriptionRenewalNote,
        setPrescriptionState,
        setPrescriptionRenewalNote,
        onEditMedicationPopupOpen,
        onDenyClick,
        addRelatedPrescriptionRenewalMedications,
        removeRelatedPrescriptionRenewalMedications,
        setRelatedPrescriptionRenewalMedications,
        setIsDirty
    }), [
        relatedPrescriptionRenewalMedications, prescriptionState, isPrescriptionRenewalCompleted, prescriptionRenewalNote,
        onEditMedicationPopupOpen, onDenyClick, addRelatedPrescriptionRenewalMedications, removeRelatedPrescriptionRenewalMedications
    ]);

    const toggleFaxAndPrintPopup = (): void => {
        if (isFaxAndPrintPopupOpen)
            setIsPrescriptionRenewalPrescribed(!isPrescriptionRenewalPrescribed);

        setIsFaxAndPrintPopupOpen(!isFaxAndPrintPopupOpen);
    };

    const completePrescription = async (actionMode: ExportMode, conflictingMedications: DSQConflitingMedication[] = null): Promise<void> => {
        if (!sessionStore.hasPermissionsToPrescribe()) {
            sessionStore.showPopup({
                title: getText("ErrorMLabel"),
                content: getText("PermissionsPrescriptionErrorLabel")
            });
            return;
        }

        if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning && !sessionStore.signOnPrintAccess) {
            sessionStore.showPopup({
                title: getText("ErrorMLabel"),
                content: getText("SignaturePrescriptionErrorLabel")
            });
            return;
        }

        if (!sessionStore.getCurrentEmployeeDefaultSpecializationLookupId()) {
            sessionStore.showPopup({
                title: getText("ErrorMLabel"),
                content: getText("PrescriptionErrorNoSpecialityLabel")
            });
            return;
        }

        setIsPrescriptionRenewalPrescribed(!isPrescriptionRenewalPrescribed);

        const isFavorite = prescriptionState?.get("IsFavorite") ?? false;
        const isDqsRequest = sessionStore.isMsaActive && patientProfile.isPatientDSQSynced() && !sessionStore.DSQPrescriptionDisabled && cacheStore.ClinicSettings?.EnableDSQPrescription;
        const selectedMedications = prescriptionState?.get("MedicationModels")?.toArray().map(medicationModel => new MedicalItemMedicationAndPrescription(medicationModel.Model));

        let originalDsqMeds: MedicalItemMedicationAndPrescription[];

        if (isDqsRequest && selectedMedications?.any()) {
            const dsqMedications = prescriptionState?.get("DSQMedicationModels")?.toArray()?.filter(x => x.Model.DsqId);
            if (dsqMedications?.any()) {
                originalDsqMeds = dsqMedications
                    .filter(dsqMed => selectedMedications.any(m => m.OriginalMedicationId === dsqMed.Model.Id) && ["1", "2", "3"].contains(dsqMed.Model.PrescriptionTab_PendingStatus))
                    .map(medicationModel => new MedicalItemMedicationAndPrescription(medicationModel.Model));
            }
        }
        const response = await completePrescriptionRenewal(
            prescriptionRenewalId,
            patientProfile?.PatientResponse?.ContactFacet_id,
            patientCpp.get("PatientContactId"),
            selectedMedications ?? [],
            currentDocument.documentId,
            relatedPrescriptionRenewalMedications,
            prescriptionRenewalNote,
            isFavorite,
            isDqsRequest,
            conflictingMedications,
            originalDsqMeds
        );

        if (response.Status === 2) {
            sessionStore.showPopup({
                title: getText("ErrorLabel"),
                content: response.Errors
            });

            setIsPrescriptionRenewalPrescribed(false);

            return;
        }
        else if (response.Status === 3) {
            if (response.Errors?.any()) {
                if (sessionStore.DSQPrescriptionError >= 3) {
                    sessionStore.DSQPrescriptionDisabled = true;
                    sessionStore.showPopup(getText("PrescriptionDsqErrorLabel"));
                }
                else {
                    sessionStore.DSQPrescriptionError = sessionStore.DSQPrescriptionError + 1;
                }
            }

            if (response.ConflictingMedications?.any()) {
                const messages = [getText("PrescriptionDsqConflictsErrorLabel"), ""]
                    .concat(response.ConflictingMedications.map(conflictMedication => "-" + conflictMedication.MyleMedicationName + "\r\n"));

                sessionStore.showPopup({
                    content: messages,
                    showCancel: false,
                    okLabel: getText("GoBackToPrescriptionLabel"),
                    altButtonLabel: getText("PrescribeLabel"),
                    altButtonAction: () => completePrescription(actionMode, response.ConflictingMedications)
                });
            }

            setIsPrescriptionRenewalPrescribed(false);

            return;
        }
        else if (response.Errors?.any()) {
            sessionStore.showPopup({
                title: getText("ErrorLabel"),
                content: response.Errors
            });

            return;
        }

        if (isFavorite) {
            cacheStore.requestReloadFavoritePrescription();
        }

        setEncounterId(response.EncounterId);
        setExportMode(actionMode);
        buildExportableFileConfig(response.PrescriptionId);
        toggleFaxAndPrintPopup();
    };

    const buildExportableFileConfig = (prescriptionId: Guid): void => {
        const exportableFile = new FileTypePrescription({
            prescriptionId: prescriptionId.toString(),
            description: getText("PrescriptionLabel"),
            date: DateTime.local().toFormat("yyyy-MM-dd")
        });

        const preferredPharmacy = patientProfile.PreferredPharmacy?.getPharmacyInfo();
        if (cacheStore.validateMylePharmacy(!!preferredPharmacy?.IsMylePharmacy) && senderFaxNumber === "") {
            exportableFile.applyPrepositionedPharmacyInfoOverlay(preferredPharmacy);
        }

        const config: IFaxPrintExportableFileConfig = {
            patientContactId: patientCpp.get("PatientContactId"),
            exportableFile: exportableFile,
            exportMode: ExportMode.Fax,
            isSyncToPortalAfterEnabled: false,
            onApplyOverlaysCallback: null,
            hideSignaturePinControl: false,
            isSaveSignatureEnabled: false
        };

        setExportableFileConfig(config);
    };

    const returnWithCommentLabel: string = getText("ReturnWithCommentLabel") ?? "";
    const onReturnWithCommentClick = (): void => {
        if (!sessionStore.hasPermissionToApproveMedicalDocument()) {
            sessionStore.showPopup({
                title: getText("ErrorMLabel"),
                content: getText("PermissionsToApproveMedicalDocumentErrorLabel")
            });

            return;
        }

        setIsReturnWithCommentPopupOpen(true);
    };

    const submitDocumentForUpdate = async (documentData: IDocumentInformation): Promise<void> => {
        const responseIsSuccess = await updateDocumentInformation(documentData);

        if (!responseIsSuccess) return;

        if (documentData.docClass !== cacheStore.FindLookupByLookupCode(DocumentClass.MedicationAndPrescriptions).LookupID) {
            navigateNextPrescriptionRenewal();
        }
        await fetchDocumentData(currentDocument.documentId);
        setIsDocumentDetailsPopupOpen(false);
    };

    const documentNotesLength = medicalDocumentData?.Model?.NoteInfoCollection?.length;
    const notesIndicatorText = ` ${documentNotesLength || "+"} `;
    const notesIndicatorCss = `bold-text${(documentNotesLength || medicalDocumentData?.Model?.IsUrgent ? " red" : "")}`;
    const documentNotes: Note[] = medicalDocumentData?.Model?.NoteInfoCollection
        .sort((a: DocumentNote, b: DocumentNote) => (new Date(b.NoteDate).getTime() - new Date(a.NoteDate).getTime()))
        .map(p => {
            const note: Note = {
                ContactId: p.ContactId,
                NoteContent: p.NoteContent,
                NoteDate: p.NoteDate,
                NoteId: p.NoteId,
                type: ""
            };
            return note;
        });

    const preferredPharmacyHelper = useMemo(() => new PreferredPharmacyHelper(patientProfile?.Addresses ?? [], sessionStore.getCurrentEmployee()), [patientProfile]);

    async function onPrint(): Promise<void> {
        if (cacheStore.ClinicSettings.EnableMedicationsPrintFilterAndHistory) {
            const prescriptionId = (exportableFileConfig.exportableFile as FileTypePrescription).prescriptionId;
            const addPrescriptionPrintHistoryResponse = await PrescriptionPrintHistoryClient.addPrescriptionPrintHistory(prescriptionId);
            const prescriptionPrintHistory = new PrescriptionPrintHistory(addPrescriptionPrintHistoryResponse.data);

            patientStore.addPrescriptionPrintHistory(prescriptionPrintHistory, patientStore.getCurrentPatientContactId());
        }
    }

    return (
        <>
            <div data-cy="prescription-renewal-view" className={styles.container}>
                {isPortalPopupOpen && (
                    <SendToPortalPopup
                        model={documentsStore.transformDocumentToPortalSharedItems(medicalDocumentData)}
                        strategy={new SendDocumentStrategy()}
                        isAsyncCallback={isSuccessed => {
                            if (isSuccessed) {
                                fetchDocumentData(currentDocument.documentId);
                                onPortalStatusClicked();
                            }
                        }}
                        onClosePopup={() => onPortalStatusClicked()}
                    />
                )}
                {isFaxAndPrintPopupOpen && (
                    <PrescriptionRenewalExportableFileFaxPopup
                        patientContactId={patientCpp?.get("PatientContactId")}
                        documentId={currentDocument.documentId}
                        exportableFile={exportableFileConfig.exportableFile}
                        onToggleFaxAndPrintPopup={() => toggleFaxAndPrintPopup()}
                        onNextPrescriptionRenewal={() => navigateNextPrescriptionRenewal()}
                        encounterId={encounterId}
                        exportMode={exportMode}
                        preferredPharmacy={patientProfile.PreferredPharmacy.getPharmacyInfo(true, preferredPharmacyHelper)}
                        attachedDocument={currentDocument}
                        onPrint={onPrint}
                    />
                )}
                {isDocumentDetailsPopupOpen && (
                    <DocumentNotesPopup
                        notes={documentNotes}
                        referenceId={medicalDocumentData?.Model?.Id}
                        model={documentCreateEditUIModel}
                        onCancelClick={() => setIsDocumentDetailsPopupOpen(false)}
                        onSaveClick={async (documentData, isDocumentApproved) => {
                            await submitDocumentForUpdate(documentData);

                            if (isDocumentApproved) {
                                navigateNextPrescriptionRenewal();
                            }
                        }}
                    />
                )}
                <div className="myle-page-header bottom-border">
                    {patientBannerModel !== null && (
                        <PatientBannerForLabs
                            Model={patientBannerModel}
                            shouldLoadLastUpcomingAppointment
                            shouldHideMemo
                            shouldHideActivationLabel
                            shouldHideChangePicture
                            shouldHideUpdateInformation
                            isCurrentEmployeeActiveSiteInBritishColumbia={sessionStore.getCurrentEmployee().getActiveSite().isSiteInProvince(CanadaState.BritishColumbia)}
                            onNewExternalCommunicationClick={() => setIsExternalCommunicationVisible(p => !p)}
                            onDropDownMenuClick={() => setPatientBanner(p => p.set("PatientDropdownVisible", !p.get("PatientDropdownVisible")))}
                            onTaskOrMessageClick={(patient: Person, isTask: boolean) => {
                                sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
                                    patient,
                                    isTask,
                                    documentInfo: medicalDocumentData
                                });
                            }}
                        />
                    )}
                </div>
                <div className={styles.subHeader}>
                    <div className={styles.vitalSign}>
                        <VitalSignsView
                            currentPatient={patientProfile}
                            cssClassName={styles.vitalSign}
                            cssClassNameDate={styles.vitalSignDate}
                            vitalSign={latestVitalSign}
                            showArrow={false}
                        />
                    </div>
                </div>
                <div className={styles.main}>
                    <div
                        ref={pdfViewerContainerRef}
                        className={`${styles.pdfViewerContainer ?? ""} ${isPdfViewerExpanded ? styles.expanded : ""}`}
                    >
                        <div className={styles.pdfViewerLinksContainer}>
                            <div>
                                <a onClick={() => setIsDocumentDetailsPopupOpen(true)} data-cy="prescriptionRenewalsView-documentDetails">
                                    [<span className={notesIndicatorCss}>{notesIndicatorText}</span>] {getText("DocumentDetailsLabel")}
                                </a>
                            </div>
                            {cacheStore.canSendFaxes() && (
                                <>
                                    <div>|</div>
                                    <div>
                                        <a
                                            className="clickable-text-link"
                                            onClick={() => onReturnWithCommentClick()}
                                        >
                                            {returnWithCommentLabel}
                                        </a>
                                    </div>
                                </>
                            )}
                        </div>
                        <PdfViewer
                            pdfSource={pdfSource}
                            selectedZoomLevelIndex={pdfZoomLevelIndex}
                            onZoomLevelChanged={newIndex => setPdfZoomLevelIndex(newIndex)}
                            onLoadDocumentSuccess={(pageNumber: number) => {
                                const invalidPageNumber: number = -1;
                                const newPageWithDetails = Array(pageNumber).fill(invalidPageNumber).map(
                                    (_p: number, curIndex: number) => new PdfPageDetails({ pageNumber: curIndex + 1 })
                                );

                                setPageWithDetails(newPageWithDetails);
                            }}
                            onPopoutPdf={() => onPdfPopoutClick(currentDocument.documentByteId, pagesWithDetails)}
                            onSetDefaultZoomLevel={(zoomLevels: number[], defaultPageWidth: number) =>
                                onSetDefaultZoomLevel(zoomLevels, defaultPageWidth)
                            }
                            onLoadPageSuccess={(pageNumber: number, pageOrientation: number) => {
                                setPageWithDetails(pageWithDetailsState => pageWithDetailsState.map(
                                    pageDetail => {
                                        if (pageDetail.pageNumber === pageNumber) {
                                            return new PdfPageDetails({ ...pageDetail, orientation: pageOrientation });
                                        }

                                        return pageDetail;
                                    }
                                ));
                            }}
                            isReadOnly
                            shouldShowPopoutInViewToolbar
                        />
                    </div>
                    <div
                        className={styles.verticalBarContainer}
                        onClick={() => setIsPdfViewerExpanded(prev => !prev)}
                    >
                        <SlideMenuBar />
                    </div>
                    <div className={styles.contentContainer}>
                        <div className={styles.navbarContainer}>
                            <PrescriptionRenewalsViewNavigationBar prescriptionRenewalId={prescriptionRenewalId} />
                        </div>
                        <div className={styles.content}>
                            <PrescriptionRenewalsViewContext.Provider value={prescriptionRenewalsViewContextValue}>
                                <PrescriptionRenewalsViewRoutes
                                    prescriptionRenewalId={prescriptionRenewalId}
                                    patientSummary={patientCpp}
                                    cleanPatientSummary={cleanPatientCpp}
                                    patientProfile={patientProfile}
                                    refreshPatientData={async () => {
                                        const patientData: GetCumulativePatientProfileByPrescriptionRenewalResponse = await fetchCumulativePatientProfile(prescriptionRenewalId);
                                        const ontarioPatientEnrollmentModel = await getOntarioPatientEnrollmentModel(patientData?.CumulativePatientProfile?.PatientInactivableId);
                                        setPatientData(patientData, true, ontarioPatientEnrollmentModel);
                                    }}
                                />
                            </PrescriptionRenewalsViewContext.Provider>
                        </div>
                    </div>
                </div>
                <div className={`${styles.footer} ws-footer`}>
                    <div className={styles.subContainer}>
                        {prescriptionRenewalInboxSettings && (
                            <PaginationViewControl
                                itemCount={prescriptionRenewalsCount}
                                isLastItem={isLastPrescriptionRenewal}
                                onPreviousItemClick={() => onPreviousPrescriptionRenewal()}
                                onNextItemClick={() => navigateNextPrescriptionRenewal()}
                                absoluteIndex={getCurrentPrescriptionRenewalIndexFromTotal()}
                                isPreviousButtonDisabled={isPreviousDisabled}
                                onLastItem={() => navigateToPrescriptionRenewalInbox()}
                            />
                        )}
                        {cacheStore.ClinicSettings_Portal.EnablePortalWebsite && medicalDocumentData &&
                        PortalHelper.shouldShowPortalShareButton(medicalDocumentData.Model?.Patients) && (
                            <PortalStatusButton
                                onClick={() => onPortalStatusClicked()}
                                {...PortalHelper.getPortalButtonPropsForShareableItem(medicalDocumentData)}
                            />
                        )
                        }
                    </div>
                    <div className={styles.subContainer}>
                        <CheckboxControl
                            label={getText("UrgentLabel")}
                            checked={isUrgent}
                            disabled
                            labelClassName={styles.urgentCheckboxLabel}
                        />
                        <ButtonControl
                            onClick={() => {
                                props.onCancelClick(prescriptionRenewalRequestData.QueryCurrentPageNumber);
                                navigate("/prescriptionrenewals");
                            }}
                            className="btn btn-default"
                            label={getText("CancelLabel")}
                            disabled={isPrescriptionRenewalPrescribed}
                            dataCy="prescription-renewal-cancel-btn"
                        />
                        {!IsNullOrUndefined(prescriptionRenewalStatus) && (
                            isPrescriptionRenewalCompleted ? (
                                <strong>
                                    {getText("Completed2Label")}
                                </strong>
                            ) : (
                                <>
                                    <ButtonControl
                                        onClick={async () => {
                                            await onSavePrescriptionRenewalDetails();
                                            navigateNextPrescriptionRenewal();
                                        }}
                                        label={getText("SaveAsDraftLabel")}
                                        className="btn btn-primary"
                                        disabled={!isDirty || isPrescriptionRenewalPrescribed || hasDsqMedications}
                                    />
                                    {sessionStore.hasPermissionsToSeePrescribeActions() && (
                                        <>
                                            <ButtonControl
                                                onClick={() => completePrescription(ExportMode.Sign)}
                                                label={getText("SignLabel")}
                                                className="btn btn-primary"
                                                disabled={isButtonDisabled() || areAllMedicationsDenied}
                                            />
                                            <ButtonControl
                                                onClick={() => completePrescription(ExportMode.Print)}
                                                label={getText("PrintLabel")}
                                                className="btn btn-primary"
                                                disabled={isButtonDisabled()}
                                                dataCy="prescription-renewal-print-btn"
                                            />
                                            <ButtonControl
                                                onClick={() => completePrescription(ExportMode.Fax)}
                                                label={getText("FaxVerbLabel")}
                                                className="btn btn-primary"
                                                disabled={isButtonDisabled()}
                                            />
                                        </>
                                    )}
                                </>
                            )
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.popupContainer}>
                {relatedMedicationToDeny && (
                    <PrescriptionRenewalsViewDenyPopup
                        relatedMedication={relatedMedicationToDeny}
                        onSave={(rejectReason: string, rejectNotes: string) => onDenyPopupSave(rejectReason, rejectNotes)}
                        onCancel={() => onDenyPopupClose()}
                        isReadonly={isPrescriptionRenewalCompleted}
                    />
                )}
                {medicationModelToUpdate && (
                    <PrescriptionRenewalsViewEditMedicationPopup
                        title={getText("AdjustMedicationLabel")}
                        initialMedicationModel={medicationModelToUpdate}
                        onCancel={() => onEditMedicationPopupClose()}
                        onSave={(updatedMedicationModel: MedicationModel) => {
                            const currentMedicationModels: Immutable.List<MedicationModel> = prescriptionState.get("MedicationModels");
                            const updatedMedicationModels = currentMedicationModels.map(p => {
                                if (p.Model.Id === updatedMedicationModel.Model.Id) {
                                    return updatedMedicationModel.Clone(false, false, true);
                                }

                                return p.Clone(false, false, true);
                            }).toList();

                            const updatedRelatedMedications = relatedPrescriptionRenewalMedications.map(p => {
                                if (p.medicationId === updatedMedicationModel.Model.Id) {
                                    return new PrescriptionRenewalMedications({ ...p, medicationId: updatedMedicationModel.Model.Id });
                                }

                                return p;
                            });

                            setPrescriptionState(current => current.set("MedicationModels", updatedMedicationModels));
                            setRelatedPrescriptionRenewalMedications(updatedRelatedMedications);
                            onEditMedicationPopupClose();
                            setIsDirty(true);
                        }}
                    />
                )}
                {isReturnWithCommentPopupOpen && (
                    cacheStore.ClinicSettings.EnableFaxCoverSheet ? (
                        <OutboundFaxCreationPopup
                            onCancel={() => setIsReturnWithCommentPopupOpen(false)}
                            onSubmit={(faxDetails, onCompleted) => returnWithComment(faxDetails, onCompleted)}
                            popupTitle={returnWithCommentLabel}
                            initialFaxNumber={senderFaxNumber}
                            showX
                            returnFaxSender
                        />
                    ) : (
                        <ReturnFaxToSenderPopup
                            title={returnWithCommentLabel}
                            onAccept={faxDetails => returnWithComment(faxDetails)}
                            onCancel={() => setIsReturnWithCommentPopupOpen(false)}
                            initialFaxNumber={senderFaxNumber ?? ""}
                            clickableTextContext="PrescriptionRenewalNotes"
                        />
                    )
                )}

                {isExternalCommunicationVisible && (
                    <ExternalMessageFormPopupComponent
                        selectedPatient={patientProfile ? InternalPatientResponse.fromPerson(Person.FromPatient(patientProfile)) : null}
                        onClose={() => setIsExternalCommunicationVisible(false)}
                        onSend={() => setIsExternalCommunicationVisible(false)}
                        selectedAttachments={exportableDocumentByteFileModel ? [exportableDocumentByteFileModel] : []}
                    />
                )}
            </div>
        </>
    );
}

export { PrescriptionRenewalsView, PrescriptionRenewalsViewContext };
